import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CertificateDetail } from '../../models/verify-signature-response.model';
import { dnCommonName, parseDnAttributes, parseSubjectDN } from '../../core/utils/cert-subject.utils';

export interface CertificateInfoModalData {
  cert: CertificateDetail;
  lastCheckDate?: Date | null;
}

@Component({
    selector: 'app-certificate-info-modal',
    templateUrl: './certificate-info-modal.component.html',
    styleUrls: ['./certificate-info-modal.component.css'],
    standalone: false
})
export class CertificateInfoModalComponent {
  showRawDn = false;

  constructor(
    public dialogRef: MatDialogRef<CertificateInfoModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CertificateDetail | CertificateInfoModalData
  ) {}

  get cert(): CertificateDetail {
    if (!this.data || typeof this.data !== 'object') return {} as CertificateDetail;
    return 'cert' in this.data
      ? (this.data as CertificateInfoModalData).cert
      : (this.data as CertificateDetail);
  }

  get lastCheckDate(): Date | null | undefined {
    return this.data && typeof this.data === 'object' && 'cert' in this.data
      ? (this.data as CertificateInfoModalData).lastCheckDate
      : undefined;
  }

  get subjectCn(): string {
    return dnCommonName(this.cert?.asunto) || this.cert?.titular || '—';
  }

  get issuerCn(): string {
    return dnCommonName(this.cert?.emisor) || this.cert?.emisor || '—';
  }

  get subjectEmail(): string | null {
    return parseSubjectDN(this.cert?.asunto).email || null;
  }

  get subjectOrg(): string | null {
    return parseDnAttributes(this.cert?.asunto).find((a) => a.key.toUpperCase() === 'O')?.value || null;
  }

  get issuerOrg(): string | null {
    return parseDnAttributes(this.cert?.emisor).find((a) => a.key.toUpperCase() === 'O')?.value || null;
  }

  get timestampDetailText(): string {
    const cert = this.cert;
    if (cert?.timestamped) {
      const when = cert.timestampDate ? this.formatDisplayDate(cert.timestampDate) : null;
      return when
        ? `La hora de la firma procede de una estampa de tiempo confiable (${when}).`
        : 'La hora de la firma procede de una estampa de tiempo confiable (TSA).';
    }
    return 'La hora de la firma procede del reloj del equipo del firmante.';
  }

  get ltvDetailText(): string {
    const cert = this.cert;
    if (cert?.ltvEnabled === true) {
      return 'La firma incluye evidencia LTV embebida en el documento.';
    }
    if (cert?.ltvEnabled === null || cert?.ltvEnabled === undefined) {
      return 'LTV no aplica para este tipo de firma.';
    }
    return `La firma no está activada para LTV y caducará después de ${this.formatValidoHastaForLTV(cert?.validoHasta)}.`;
  }

  toggleRawDn(): void {
    this.showRawDn = !this.showRawDn;
  }

  formatDisplayDate(dateValue: string | Date | null | undefined): string {
    if (!dateValue || dateValue === 'N/A') return '—';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return String(dateValue);
    if (date.getFullYear() < 1990 || date.getFullYear() > 2100) {
      return String(dateValue);
    }
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatLastCheckDate(date: Date | null | undefined): string {
    if (date == null) return '—';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const tz = -d.getTimezoneOffset();
    const tzSign = tz >= 0 ? '+' : '-';
    const tzMin = Math.abs(tz) % 60;
    const tzHour = Math.floor(Math.abs(tz) / 60);
    return `${y}.${m}.${day} ${h}:${min}:${s} ${tzSign}${String(tzHour).padStart(2, '0')}'${String(tzMin).padStart(2, '0')}'`;
  }

  formatValidoHastaForLTV(validoHasta: string | null | undefined): string {
    if (!validoHasta || validoHasta === 'N/A') return '—';
    const d = new Date(validoHasta);
    if (Number.isNaN(d.getTime())) return validoHasta;
    if (d.getFullYear() < 1990 || d.getFullYear() > 2100) return validoHasta;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
  }
}
